// Lightweight parser for the freehand-SQL data-source mode.
// The runtime BI engine accepts plain SQL with two kinds of placeholders:
//   ?.<var_name>         user prompt — needs a CriteriaRule
//   ?.@<TokenName>       system input — resolved silently
// We extract:
//   (a) the column list shown to the user — derived from SELECT aliases
//   (b) the runtime variables the user must label/type — `?.<var_name>` only
// This is intentionally regex-based, not a full SQL parser. The goal is to
// keep the designer responsive while the user types and to surface enough
// metadata for ColumnGroup placement.

export interface ParsedSqlColumn {
  // Display alias (the AS <alias> portion, or the bare column name when no AS).
  // This is what the user drags into ColumnGroups.
  alias: string
  // The full SELECT expression (e.g. "SUM(COALESCE(t.x,0))"). May equal alias.
  expression: string
  // Best-effort type inference from the expression:
  //   number  → SUM/COUNT/AVG/MIN/MAX/numeric literal
  //   date    → references a *_DATE column or DATE literal
  //   string  → default
  inferredType: 'string' | 'number' | 'date'
  // True when the expression is wrapped in an aggregate function — the
  // editor flags these as numeric measures (EXPRESSIONTYPE "G").
  isAggregate: boolean
}

export interface ParsedSqlVariable {
  // The placeholder name without the leading "?." or "?.@"
  name: string
  // True when the placeholder was "?.@..." — a system input that does NOT
  // need a CriteriaRule (resolved at runtime from session/date tokens).
  isSystem: boolean
  // Parsed from the insight-bi binding suffix `?.name.<type>.<default>`.
  type?: 'string' | 'number' | 'date'
  default?: string
}

// Data-type keywords accepted in the `<type>` slot of a binding suffix.
const TYPE_KEYWORDS: Record<string, 'string' | 'number' | 'date'> = {
  date: 'date', d: 'date',
  number: 'number', n: 'number', num: 'number', int: 'number', integer: 'number',
  decimal: 'number', float: 'number',
  string: 'string', s: 'string', c: 'string', char: 'string', varchar: 'string', text: 'string',
}

/** Split a binding suffix (with leading dot) into `{type, default}`. */
export function parseBindingSuffix(suffix: string | undefined): {
  type?: 'string' | 'number' | 'date'; default?: string
} {
  if (!suffix) return {}
  const parts = suffix.replace(/^\./, '').split('.')
  if (parts.length === 0) return {}
  const first = (parts[0] || '').toLowerCase()
  if (first in TYPE_KEYWORDS) {
    const rest = parts.slice(1).join('.')
    return { type: TYPE_KEYWORDS[first], default: rest || undefined }
  }
  const def = parts.join('.')
  return { default: def || undefined }
}

/** Build the binding suffix string for a placeholder, omitting empty parts.
 *  `?.name` → '' · type only → '.date' · default → '.<type>.<default>'. */
export function buildBindingSuffix(
  type: 'string' | 'number' | 'date' | undefined,
  defaultValue: string | undefined,
): string {
  const def = (defaultValue ?? '').trim()
  if (def !== '') return `.${type || 'string'}.${def}`
  if (type && type !== 'string') return `.${type}`
  return ''
}

const AGG_RE = /^\s*(SUM|COUNT|AVG|MIN|MAX)\s*\(/i

// A star select item: `*` or `alias.*` — it names no column, it stands for
// every column of the (aliased) table. Only the database can expand it, so the
// parser reports it as `hasStar` and the designer asks the server for the real
// result columns instead of offering a literal `*` chip.
const STAR_RE = /^(?:[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?\*$/

export function parseSourceSql(sql: string): {
  columns: ParsedSqlColumn[]
  variables: ParsedSqlVariable[]
  /** True when the SELECT list contains `*` / `t.*` — the parsed `columns`
   *  are then INCOMPLETE (they omit whatever the star stands for). */
  hasStar: boolean
} {
  if (!sql || typeof sql !== 'string') {
    return { columns: [], variables: [], hasStar: false }
  }

  const items = selectListItems(sql)
  return {
    columns: items.filter((e) => !STAR_RE.test(e)).map(parseColumnExpression)
      .filter((c) => c.alias),
    variables: extractVariables(sql),
    hasStar: items.some((e) => STAR_RE.test(e)),
  }
}

/** The raw SELECT-list expressions, one per top-level comma.
 *
 *  The list runs from the OUTER query's SELECT to its own FROM — both found at
 *  parenthesis depth 0, outside string literals, quoted identifiers and
 *  comments. A scalar subquery in the list (`(SELECT a || ' ' || b FROM emp
 *  WHERE …) AS name`) has its own SELECT/FROM one level down, so it neither
 *  ends the list early nor has its commas split; a `WITH` query's CTE bodies
 *  are parenthesised, so the main SELECT is still the first one at depth 0. */
function selectListItems(sql: string): string[] {
  const start = topLevelKeyword(sql, 'select', 0)
  if (start < 0) return []
  const from = topLevelKeyword(sql, 'from', start + 6)
  if (from < 0) return []
  const list = sql.slice(start + 6, from).trim().replace(/^distinct\s+/i, '')
  return splitTopLevelCommas(list)
}

/** Walk `sql`, calling `visit(i, depth)` for every character that is SQL
 *  text — skipping string literals, quoted identifiers and comments. The
 *  visitor returns true to stop; the stop index is returned (-1 if none). */
function scanSql(sql: string, from: number, visit: (i: number, depth: number) => boolean): number {
  let depth = 0
  for (let i = from; i < sql.length; i++) {
    const ch = sql[i]
    if (ch === "'" || ch === '"') {
      // '' / "" inside a literal is an escaped quote, not its end.
      let j = i + 1
      while (j < sql.length) {
        if (sql[j] === ch) {
          if (sql[j + 1] === ch) { j += 2; continue }
          break
        }
        j++
      }
      i = j
      continue
    }
    if (ch === '-' && sql[i + 1] === '-') {
      const nl = sql.indexOf('\n', i)
      if (nl < 0) break
      i = nl
      continue
    }
    if (ch === '/' && sql[i + 1] === '*') {
      const end = sql.indexOf('*/', i + 2)
      if (end < 0) break
      i = end + 1
      continue
    }
    if (ch === '(') depth++
    else if (ch === ')') depth--
    if (visit(i, depth)) return i
  }
  return -1
}

/** Index of `word` as a whole keyword at parenthesis depth 0, or -1. */
function topLevelKeyword(sql: string, word: string, from: number): number {
  const n = word.length
  const isIdent = (c: string | undefined) => !!c && /[A-Za-z0-9_$#.]/.test(c)
  return scanSql(sql, from, (i, depth) =>
    depth === 0
    && sql.substr(i, n).toLowerCase() === word
    && !isIdent(sql[i - 1])
    && !isIdent(sql[i + n]),
  )
}

function splitTopLevelCommas(s: string): string[] {
  const out: string[] = []
  let last = 0
  scanSql(s, 0, (i, depth) => {
    if (s[i] === ',' && depth === 0) {
      if (s.slice(last, i).trim()) out.push(s.slice(last, i).trim())
      last = i + 1
    }
    return false
  })
  if (s.slice(last).trim()) out.push(s.slice(last).trim())
  return out
}

function parseColumnExpression(raw: string): ParsedSqlColumn {
  // Strip surrounding whitespace.
  const expr = raw.trim()
  // Detect AS alias: "<expr> AS <name>" or "<expr> <name>" (last token).
  let alias = ''
  let exprPart = expr
  const asMatch = expr.match(/^(.+?)\s+as\s+([A-Za-z_][A-Za-z0-9_]*)\s*$/i)
  if (asMatch) {
    exprPart = asMatch[1].trim()
    alias = asMatch[2]
  } else {
    // No AS — try last identifier as alias if there are at least 2 tokens
    // and the expression isn't wrapped in parens.
    const tokens = expr.split(/\s+/)
    if (tokens.length > 1 && /^[A-Za-z_][A-Za-z0-9_]*$/.test(tokens[tokens.length - 1])) {
      alias = tokens[tokens.length - 1]
      exprPart = tokens.slice(0, -1).join(' ')
    } else {
      // Bare column "table.col" or "col" — alias is the last identifier.
      const dotMatch = expr.match(/([A-Za-z_][A-Za-z0-9_]*)\s*$/)
      alias = dotMatch ? dotMatch[1] : expr
      exprPart = expr
    }
  }
  const isAggregate = AGG_RE.test(exprPart)
  return {
    alias,
    expression: exprPart,
    inferredType: inferType(exprPart, isAggregate),
    isAggregate,
  }
}

function inferType(expr: string, isAggregate: boolean): 'string' | 'number' | 'date' {
  if (isAggregate) return 'number'
  if (/_DATE\b/i.test(expr) || /\bDATE\s*\(/i.test(expr)) return 'date'
  if (/\b(VALUE|AMOUNT|QTY|QUANTITY|PRICE|RATE|COUNT|TOTAL|SUM|AVG)\b/i.test(expr)) return 'number'
  return 'string'
}

function extractVariables(sql: string): ParsedSqlVariable[] {
  const out: ParsedSqlVariable[] = []
  const seen = new Set<string>()
  // ?.@<TokenName>  — system input
  const sysRe = /\?\.@([A-Za-z_][A-Za-z0-9_]*)/g
  // ?.<var_name>[.<type>.<default>]  — user prompt (must NOT be preceded by @)
  const userRe = /\?\.(?!@)([A-Za-z_][A-Za-z0-9_]*)((?:\.[^\s,()=<>!]+)+)?/g

  let m: RegExpExecArray | null
  while ((m = sysRe.exec(sql)) !== null) {
    const key = `@${m[1]}`
    if (!seen.has(key)) {
      seen.add(key)
      out.push({ name: m[1], isSystem: true })
    }
  }
  while ((m = userRe.exec(sql)) !== null) {
    const key = m[1]
    if (!seen.has(key)) {
      seen.add(key)
      const { type, default: def } = parseBindingSuffix(m[2])
      out.push({ name: m[1], isSystem: false, type, default: def })
    }
  }
  return out
}

// Build a friendly label from a snake_case variable name:
//   emp_from → Emp From
export function variableLabel(name: string): string {
  return name
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ')
}
